import { useMemo } from "react";
import { ViewMode } from "../../shared/use-view-mode";
import useProgress, { PageSavedMap } from "../../shared/use-progress";
import { UseImporterAnnualActivityAPI } from "./use-importer-annual-activity-api";

interface UseImporterAnnualActivityProgressProps {
    apiCtrl: PopulatedProps<UseImporterAnnualActivityAPI, 'submission'>;
    viewMode: ViewMode;
}

// One key per data step — Declaration (submit) step doesn't need one.
const ORDERED_PAGE_SAVED_KEYS = [
    'importsPageSaved',
    'storagePageSaved',
    'enforceableArrangementsPageSaved',
    'australianControllingCorporationPageSaved',
] as const;

type ImporterPageSavedKey = typeof ORDERED_PAGE_SAVED_KEYS[number];

function useImporterAnnualActivityProgress({apiCtrl, viewMode}: UseImporterAnnualActivityProgressProps) {

    // Build a synthetic per-step saved map from the single dataPageSaved flag.
    // Once the API provides individual flags you can replace this with the real values.
    const syntheticFormData = useMemo((): PageSavedMap<ImporterPageSavedKey> => {
        const saved = Boolean(apiCtrl.submission.submissionFormData.dataPageSaved);
        return {
            importsPageSaved: saved,
            storagePageSaved: saved,
            enforceableArrangementsPageSaved: saved,
            australianControllingCorporationPageSaved: saved,
        };
    }, [apiCtrl.submission.submissionFormData.dataPageSaved]);

    const steps = useMemo(() => {
        return [
            {
                kind: 'data' as const,
                label: 'MSO importer - Imports',
                hasData: syntheticFormData.importsPageSaved,
            },
            {
                kind: 'data' as const,
                label: 'MSO importer - Storage',
                hasData: syntheticFormData.storagePageSaved,
            },
            {
                kind: 'data' as const,
                label: 'MSO importer - Enforceable arrangements',
                hasData: syntheticFormData.enforceableArrangementsPageSaved,
            },
            {
                kind: 'data' as const,
                label: 'MSO importer - Australian Controlling Corporation',
                hasData: syntheticFormData.australianControllingCorporationPageSaved,
            },
            {
                kind: 'submit' as const,
                label: 'Declaration',
                hasData: null,
            },
        ];
    }, [syntheticFormData]);

   const progressCtrl = useProgress({
    orderedFormDataPageSavedKeys: [...ORDERED_PAGE_SAVED_KEYS],
    steps,
    submissionFormData: syntheticFormData,
    submissionStatus: apiCtrl.submission.dataSubmission.status,
    viewMode
});

    return progressCtrl;
}

export default useImporterAnnualActivityProgress;

export type UseImporterAnnualActivityProgress = ReturnType<typeof useImporterAnnualActivityProgress>;